import cx from "classnames";
import { ControlledTextArea } from "components/inputs/TextArea/ControlledTextArea";
import { throttle } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import type { Path } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { CostProjectionDataType, MSADataType } from "types/DataTypes";
import { useSelector } from "utils/redux";

type Props = {
  service: MSADataType | CostProjectionDataType;
  onSave: (field: string | null, value: unknown) => Promise<void>;
};

type FormValues = {
  legal_overview_notes: string;
  legal_exclusions_notes: string;
  legal_cost_exposure_warning_notes: string;
  legal_cost_containment_notes: string;
};

const hasLegalNotes = (
  service: MSADataType | CostProjectionDataType,
): service is MSADataType & {
  legal_overview_notes?: string;
  legal_exclusions_notes?: string;
  legal_cost_exposure_warning_notes?: string;
  legal_cost_containment_notes?: string;
} => {
  return (
    "legal_overview_notes" in service ||
    "legal_exclusions_notes" in service ||
    "legal_cost_exposure_warning_notes" in service ||
    "legal_cost_containment_notes" in service
  );
};

const FutureMedicalLegalNotes = ({ service, onSave }: Props) => {
  const showLegalNotes = useSelector((state) => state.drawer.showLegalNotes);

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      legal_overview_notes: hasLegalNotes(service)
        ? service.legal_overview_notes || ""
        : "",
      legal_exclusions_notes: hasLegalNotes(service)
        ? service.legal_exclusions_notes || ""
        : "",
      legal_cost_exposure_warning_notes: hasLegalNotes(service)
        ? service.legal_cost_exposure_warning_notes || ""
        : "",
      legal_cost_containment_notes: hasLegalNotes(service)
        ? service.legal_cost_containment_notes || ""
        : "",
    },
  });

  // update form values when service changes
  useEffect(() => {
    if (hasLegalNotes(service)) {
      const fields: Record<keyof FormValues, string> = {
        legal_overview_notes: service.legal_overview_notes || "",
        legal_exclusions_notes: service.legal_exclusions_notes || "",
        legal_cost_exposure_warning_notes:
          service.legal_cost_exposure_warning_notes || "",
        legal_cost_containment_notes:
          service.legal_cost_containment_notes || "",
      };

      Object.entries(fields).forEach(([field, value]) => {
        setValue(field as Path<FormValues>, value);
      });
    }
  }, [service, setValue]);

  // throttle the actual API saves
  const throttledSave = useMemo(
    () =>
      throttle(
        (field: string | null, value: unknown) => {
          onSave(field, value);
        },
        2000,
        { leading: true, trailing: true },
      ),
    [onSave],
  );

  const handleFieldSave = useCallback(
    async (field: keyof FormValues, value: string) => {
      throttledSave(field, value);
    },
    [throttledSave],
  );

  return (
    <div
      className={cx("documents notes-msa flex jc-se", {
        open: showLegalNotes,
      })}
    >
      <div className="documents-wrapper notes">
        <div className="swim-wrapper h-100">
          <div className="grid-layout no-gap-tb swimlanes msa-notes">
            <div className="swim-headers">Legal Notes</div>

            <div className="swim-column">
              <div className="data-block">
                <div className="data-block-header">Legal Notes</div>

                <div className="grid-layout gl-1 notes-grid">
                  <ControlledTextArea
                    control={control}
                    name="legal_overview_notes"
                    label="Legal Overview"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("legal_overview_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="legal_exclusions_notes"
                    label="Legal Exclusions"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "legal_exclusions_notes",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="legal_cost_exposure_warning_notes"
                    label="Legal Cost Exposure Warning"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "legal_cost_exposure_warning_notes",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="legal_cost_containment_notes"
                    label="Legal Cost Containment"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "legal_cost_containment_notes",
                        value.toString(),
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FutureMedicalLegalNotes;
